import React, { useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { useGetTask } from '../../api/tasks';
import { useUserProfile } from '../../api/profile';
import { TaskOverview } from './TaskOverview';
import { TaskExecutionHistory } from './TaskExecutionHistory';
import { TaskLastExecution } from './TaskLastExecution';
import { TaskActivities } from './TaskActivities';
import { TaskMetrics } from './TaskMetrics';
import { Tab, Tabs } from '../../components/common/Tabs';
import { LoadingState } from '../../components/common/LoadingState';
import { ErrorMessage } from '../../components/common/ErrorMessage';
import { Feedback } from '@plerionhq/design-system';
import { DeleteTaskModal } from '../../components/modals/DeleteTaskModal';
import { ArrowLeftIcon, TrashIcon } from '@heroicons/react/24/outline';
import { DEFAULT_TASK_TYPE } from './utils';
import { TaskStatusBadge } from '../../components/common/TaskStatusBadge';

import { useTranslation } from 'react-i18next';

interface TaskDetailPageProps {
  taskId: string;
}

export const TaskDetailPage: React.FC<TaskDetailPageProps> = ({ taskId }) => {
  const { t } = useTranslation('pleri/pages');
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [feedback] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const {
    data: task,
    isLoading,
    error,
  } = useGetTask(DEFAULT_TASK_TYPE, taskId);

  const { data: userProfile } = useUserProfile();

  if (isLoading) {
    return (
      <div className="flex-1 p-6">
        <LoadingState text={t('loadingTaskDetails')} />
      </div>
    );
  }

  if (error || !task) {
    return (
      <div className="flex-1 p-6">
        <ErrorMessage
          message={(error as Error)?.message || t('failedToLoadTaskDetails')}
          onDismiss={() => navigate({ to: '/tasks' })}
        />
      </div>
    );
  }

  const tabs = [
    {
      id: 'overview',
      label: t('common:overview'),
      dataTestId: 'overview-tab',
      content: <TaskOverview task={task} userProfile={userProfile} />,
    },
    {
      id: 'history',
      label: t('executionHistory'),
      dataTestId: 'history-tab',
      content: <TaskExecutionHistory scope={task.scope} taskId={task.id} />,
    },
    {
      id: 'last-execution',
      label: t('lastExecution'),
      dataTestId: 'last-execution-tab',
      content: <TaskLastExecution taskId={task.id} />,
    },
    {
      id: 'activities',
      label: t('activities'),
      dataTestId: 'activities-tab',
      content: <TaskActivities taskId={task.id} />,
    },
    task.taskType === 'CODE_FIX' && {
      id: 'metrics',
      label: t('metrics'),
      dataTestId: 'metrics-tab',
      content: <TaskMetrics taskId={task.id} />,
    },
  ].filter(Boolean) as Tab[];

  return (
    <div className="flex-1 bg-white dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-900">
        <div className="mx-auto max-w-7xl px-6 py-6">
          {/* Breadcrumb */}
          <nav className="mb-4 flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
            <Link
              to="/tasks"
              className="transition-colors hover:text-orange-600"
            >
              {t('tasks')}
            </Link>
            <span>/</span>
            <span className="text-gray-900 dark:text-gray-100">
              {task.name}
            </span>
          </nav>

          {/* Title and Actions */}
          <div className="flex items-start justify-between">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-3">
                <h1 className="text-3xl leading-tight font-semibold text-gray-900 dark:text-gray-100">
                  {task.name}
                </h1>
                <TaskStatusBadge status={task.status} />
              </div>
            </div>
            <div className="ml-4 flex items-center gap-2">
              <Link
                to="/tasks"
                className="flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-300 dark:hover:bg-gray-800"
              >
                <ArrowLeftIcon className="h-4 w-4" />
                {t('common:back')}
              </Link>
              {task.scope !== 'system' && (
                <button
                  onClick={() => setDeleteModalOpen(true)}
                  className="flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:border-red-300 hover:bg-red-50 hover:text-red-700 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-300"
                  data-testid="delete-task"
                >
                  <TrashIcon className="h-4 w-4" />
                  {t('common:delete')}
                </button>
              )}
            </div>
          </div>

          {feedback && (
            <div className="mt-4">
              <Feedback variant={feedback.type}>{feedback.message}</Feedback>
            </div>
          )}
        </div>
      </div>
      {/* Content */}
      <div className="mx-auto max-w-7xl px-6 pb-6">
        <Tabs
          tabs={tabs}
          activeTabId={activeTab}
          onTabChange={setActiveTab}
          className="space-y-6"
        />
      </div>
      {/* Delete Task Modal */}
      {task && (
        <DeleteTaskModal
          isOpen={deleteModalOpen}
          taskId={taskId}
          taskName={task.name}
          onClose={() => setDeleteModalOpen(false)}
        />
      )}
    </div>
  );
};
